import { Box, Button, ChevronDown, ChevronUp, Text } from '../../../../../mads';
import { LinearGradient } from '../../../../../shared/components/LinearGradient/linear-gradient';
import { useI18n } from '../../../../../shared/I18n';

import { colors } from '@indeed/ifl-tokens';
import React, { useCallback, useState } from 'react';
import { Platform, type ViewStyle } from 'react-native';

const fadeStyle: ViewStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 50,
};

function VerticalFade() {
    return (
        <LinearGradient
            colors={['rgba(255, 255, 255, 0.0)', 'rgba(255, 255, 255, 1.0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={fadeStyle}
            pointerEvents="none"
        />
    );
}
export type HiddenPreviewProps = {
    children: React.ReactNode;
    height?: number;
    hidden?: boolean;
    onHiddenChange?: (hidden: boolean) => void;
};

export function HiddenPreview({
    children,
    height = 100,
    hidden: controlledHidden,
    onHiddenChange,
}: HiddenPreviewProps) {
    const { gettext } = useI18n();
    const [internalHidden, setInternalHidden] = useState(true);

    // Use controlled value if provided, otherwise use internal state
    const hidden = controlledHidden ?? internalHidden;

    const handleReveal = useCallback(() => {
        if (onHiddenChange) {
            onHiddenChange(!hidden);
        } else {
            setInternalHidden(!hidden);
        }
    }, [onHiddenChange, hidden]);

    return (
        <>
            {/*
             * Accessibility props for iOS VoiceOver, Android TalkBack, and web screen readers:
             * - accessible={false}: Prevents grouping children into one element
             * - importantForAccessibility: 'no' when hidden (iOS native & Android), 'auto' when revealed
             * - aria-hidden: true when hidden (Android native & all web screen readers)
             * - focusable: Supports tab navigation
             * This allows screen readers to navigate individual list items/paragraphs when revealed.
             */}
            <Box
                sx={hidden ? { height, position: 'relative', overflow: 'hidden' } : { flex: 1 }}
                accessible={Platform.OS === 'android' ? !hidden : false}
                importantForAccessibility={hidden ? 'no' : 'auto'}
                {...(hidden && { 'aria-hidden': true })}
                focusable={true}
            >
                {children}
                {hidden && <VerticalFade />}
            </Box>

            <Button
                variant="text"
                iconAfter={
                    hidden ? (
                        <ChevronDown color={colors.foreground.primary['[base]']} />
                    ) : (
                        <ChevronUp color={colors.foreground.primary['[base]']} />
                    )
                }
                onPress={handleReveal}
                ariaLabel={hidden ? gettext('Show more') : gettext('Show less')}
            >
                <Text>{hidden ? gettext('Show more') : gettext('Show less')}</Text>
            </Button>
        </>
    );
}
